import {
  type Column,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type Row,
  type RowData,
  type Table,
  useReactTable,
} from "@tanstack/react-table";
import { useImmer } from "use-immer";
import {
  type CSSProperties,
  Fragment,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import classes from "./TanStackTable.module.less";
import classNames from "classnames";
import { LinkButton, LinkType } from "~/common/components/Link";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { getCssSize, getSize, getWidth } from "~/common/components/Table/TanStackTableUtils";
import { type TableOptions } from "@tanstack/table-core";
import { IconSize } from "~/common/components/icon/IconSize";
import { PopperTooltip } from "~/common/components/Tooltip";

import { isNonNull } from "~/common/utils/reducers";
import { TableScrollController } from "~/common/components/Table/TableScrollController";

const VARIANTS = {
  lines: classes.tableLines,
  stripes: classes.tableStripes,
  clear: classes.tableClear,
  product: classes.tableStripes + " " + classes.tableProducts,
  hsaDetails: classes.tableDetailHSA,
  productComparison: classes.tableStripes + " " + classes.tableProductComparison,
  "": "",
};

interface Props<TData> extends Omit<TableOptions<TData>, "getCoreRowModel"> {
  className?: string;

  data: TData[];

  rootRef?: MutableRefObject<HTMLDivElement | null>;
  columns: TableOptions<TData>["columns"];

  tableTitle?: ReactNode;
  renderDetails?: (props: { row: Row<TData> }) => ReactElement;
  variant?: "lines" | "stripes" | "clear" | "product" | "hsaDetails" | "productComparison" | "";
  fullwidth?: boolean;

  stickyHeader?: boolean;
  scrollbarOnTop?: boolean;
}

export function TanStackTable<TData>({
  data,
  columns,
  className,
  rootRef,
  renderDetails,
  tableTitle,
  variant = "lines",
  fullwidth,
  stickyHeader,
  scrollbarOnTop,
  ...rest
}: Props<TData>) {
  const isSortingEnabled = columns.some((def) => def.enableSorting);
  const table = useReactTable<TData>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: isSortingEnabled ? getSortedRowModel() : undefined,
    ...rest,
  });

  const hasHeader = !tableTitle && table.getHeaderGroups().length > 0;
  const allColumnsHaveSize = columns.every((column) => column.size);

  let stickyHeaderVariant: undefined | "simple" | "split" = undefined;
  if (stickyHeader) {
    const hasAnyPinnedColumns = table.getLeafHeaders().some((header) => header.column.getIsPinned());
    if (!hasAnyPinnedColumns) {
      stickyHeaderVariant = "simple";
    } else {
      stickyHeaderVariant = "split";

      // Has pinned columns. Do all have a set width?
      if (!allColumnsHaveSize) {
        console.error("Not implemented: sticky header with pinned columns and no size set.");
      }
    }
  }

  const nameProps = stickyHeaderVariant === "split" ? { name: "ts-table" } : {};

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);

  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    if (tableContainerRef.current) {
      const element = tableContainerRef.current;
      const fn = (e: Event) => {
        const scrollLeft = (e.target as HTMLDivElement).scrollLeft ?? 0;
        setIsScrolled(scrollLeft > 0);
      };
      element?.addEventListener("scroll", fn, { passive: true });
      return () => element?.removeEventListener("scroll", fn);
    }
  }, [tableContainerRef.current]);

  // Scroll slider state - only track dimensions for showing/hiding slider
  const [scrollDimensions, setScrollDimensions] = useImmer({
    scrollWidth: 0,
    clientWidth: 0,
  });

  // Calculate pinned left width once when columns change
  const pinnedLeftWidth = useMemo(() => {
    const pinnedLeftHeaders = table.getLeftLeafHeaders();
    let totalWidth = 0;
    pinnedLeftHeaders.forEach((header) => {
      const size = getSize(header.column);
      if (size && size.unit === "px") {
        totalWidth += size.value;
      }
    });
    return totalWidth;
  }, [columns]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate if there are scrollable columns (columns beyond pinned left)
  const hasScrollableColumns = table.getCenterLeafHeaders().length > 0 || table.getRightLeafHeaders().length > 0;
  const hasOverflow = scrollDimensions.scrollWidth > scrollDimensions.clientWidth;
  const showScrollSlider = scrollbarOnTop && hasScrollableColumns && hasOverflow;

  // Track dimensions with ResizeObserver - only for determining if slider should show
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer) {
      return;
    }

    const updateDimensions = () => {
      setScrollDimensions((draft) => {
        draft.scrollWidth = tableContainer.scrollWidth;
        draft.clientWidth = tableContainer.clientWidth;
      });
    };

    const observer = new ResizeObserver(updateDimensions);
    observer.observe(tableContainer);
    updateDimensions();

    return () => observer.disconnect();
  }, [setScrollDimensions]);

  function renderTableContainer(children: ReactNode) {
    return (
      <div
        className={classNames(
          variant !== "productComparison" && stickyHeaderVariant !== "simple" && classes.tableContainer,
          isScrolled ? "is-scrolled" : undefined,
        )}
        ref={tableContainerRef}
      >
        {children}
      </div>
    );
  }
  function renderStickyTableContainer(children: ReactNode) {
    return (
      <div
        className={classNames(
          variant !== "productComparison" && stickyHeaderVariant !== "simple" && classes.tableContainer,
          classes.sticky,
          stickyHeaderVariant === "split" && classes.hideScrollbar,
          isScrolled ? "is-scrolled" : undefined,
        )}
        ref={stickyHeaderRef}
      >
        {children}
      </div>
    );
  }

  function renderTable(...elements: ReactNode[]) {
    return (
      <table
        className={classNames(
          classes.table,
          VARIANTS[variant],
          fullwidth && classes.fullWidth,
          stickyHeaderVariant === "simple" && classes.sticky,
          allColumnsHaveSize && classes.fixedLayout,
        )}
      >
        {elements}
      </table>
    );
  }

  function renderTableHeader() {
    if (!hasHeader) {
      return null;
    }

    return (
      <thead key={"head"} className={classNames(classes.header, stickyHeaderVariant === "simple" && classes.sticky)}>
        <tr>{renderHeaderCells(table)}</tr>
      </thead>
    );
  }

  function renderTableBody() {
    return (
      <tbody key={"body"}>
        {table.getRowModel().rows.map((row, idx) => (
          <Fragment key={row.id}>
            <tr className={classNames(classes.bodyRow, idx % 2 ? classes.evenRow : classes.oddRow)}>
              {renderBodyCells(row)}
            </tr>
            {row.getIsExpanded() && (
              <tr className={classNames(classes.bodyRow, idx % 2 ? classes.evenRow : classes.oddRow)}>
                <td colSpan={row.getVisibleCells().length}>{renderDetails?.({ row })}</td>
              </tr>
            )}
          </Fragment>
        ))}
      </tbody>
    );
  }

  return (
    <div
      className={classNames(
        className,
        variant === "productComparison" && stickyHeaderVariant !== "simple" && classes.tableContainer,
      )}
      ref={rootRef}
      style={{ position: "relative" }}
    >
      {tableTitle && <div className={classes.title}>{tableTitle}</div>}
      {showScrollSlider && (
        <TableScrollController
          classes={{ root: "scroll-slider__root" }}
          tableContainerRef={tableContainerRef}
          stickyHeaderRef={stickyHeaderRef}
          pinnedLeftWidth={pinnedLeftWidth}
          stickyHeader={stickyHeader}
        />
      )}
      {stickyHeaderVariant !== "split" && renderTableContainer(renderTable(renderTableHeader(), renderTableBody()))}
      {stickyHeaderVariant === "split" && (
        <>
          {renderStickyTableContainer(renderTable(renderTableHeader()))}
          {/*{renderStickyTableContainer(renderTable(renderTableHeader(), renderTableBody({ visible: false })))}*/}
          {renderTableContainer(renderTable(renderTableBody()))}
        </>
      )}
    </div>
  );
}

const getWidthFromColumn = <T extends RowData>(column: Column<T>) => {
  const size = getSize(column);
  if (!size) {
    return undefined;
  }
  return getCssSize(size);
};

const CLASS_FACTORIES: ((isFirst: boolean, isLast: boolean) => (string | boolean)[])[] = [
  (_: boolean, isLast: boolean) => [classes.pinnedLeft, isLast && classes.pinnedLeftLast, isLast && "pinned-left-last"],
  () => [],
  (isFirst: boolean) => [classes.pinnedRight, isFirst && classes.pinnedRightFirst, isFirst && "pinned-right-first"],
];

const CELL_TYPE_CLASSES = {
  data: classes.cellData,
  action: classes.cellAction,
};

function renderHeaderCells<TData>(table: Table<TData>) {
  const cells = [table.getLeftLeafHeaders(), table.getCenterLeafHeaders(), table.getRightLeafHeaders()] as const;
  return cells.map((cells, cGroupIdx) => (
    <Fragment key={cGroupIdx}>
      {cells.map((header, idx, list) => {
        const isFirst = idx === 0;
        const isLast = list[idx] === header;
        const column = header.column;
        const meta = column.columnDef?.meta ?? {};
        const type = meta.columnType ?? "data";
        const textAlign = meta.align ?? "left";
        const sideClasses = CLASS_FACTORIES[cGroupIdx](isFirst, isLast);

        const cls = classNames(classes.headerCell, sideClasses, CELL_TYPE_CLASSES[type], meta.className);

        const style: CSSProperties = {
          width: getWidthFromColumn(column),
          textAlign: textAlign,
        };
        if (column.getIsPinned() === "left") {
          const sizes = list
            .slice(0, list.indexOf(header))
            .map((c) => c.column)
            .map(getSize);
          style.left = sizes.length > 0 && sizes.every(isNonNull) ? getWidth(sizes) : 0;
        } else if (column.getIsPinned() === "right") {
          const sizes = list
            .slice(list.indexOf(header) + 1)
            .map((c) => c.column)
            .map(getSize);
          style.right = sizes.length > 0 && sizes.every(isNonNull) ? getWidth(sizes) : 0;
        }

        const canSort = header.column.columnDef.enableSorting === true;
        const isSorted = header.column.getIsSorted();

        let content;
        if (header.isPlaceholder) {
          content = null;
        } else {
          if (!canSort) {
            content = flexRender(header.column.columnDef.header, header.getContext());
          } else {
            content = (
              <LinkButton
                variant={isSorted ? LinkType.PRIMARY : LinkType.NAVIGATION}
                onClick={() => header.column.toggleSorting(isSorted === "asc")}
                className={classes.sortHeader}
              >
                <Icon
                  type={IconType.ArrowUp}
                  size={IconSize.FONT_10}
                  className={classNames(
                    classes.sortIcon,
                    isSorted === "desc" && classes.sortIconDesc,
                    isSorted === "asc" && classes.sortIconAsc,
                  )}
                />
                <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
              </LinkButton>
            );
          }
          if (meta.tooltip) {
            content = (
              <PopperTooltip tooltipContent={meta.tooltip} className={"d-i-flex"}>
                {content}
              </PopperTooltip>
            );
          }
        }

        return (
          <th key={header.id} colSpan={header.colSpan} className={cls} style={style}>
            {content}
          </th>
        );
      })}
    </Fragment>
  ));
}
function renderBodyCells<TData>(row: Row<TData>) {
  const cells = [row.getLeftVisibleCells(), row.getCenterVisibleCells(), row.getRightVisibleCells()] as const;

  return cells.map((cells, cGroupIdx) => (
    <Fragment key={cGroupIdx}>
      {cells.map((cell, idx, list) => {
        const isFirst = idx === 0;
        const isLast = list[idx] === cell;
        const column = cell.column;
        const meta = column.columnDef?.meta ?? {};
        const type = meta.columnType ?? "data";
        const textAlign = meta.align ?? "left";
        const sideClasses = CLASS_FACTORIES[cGroupIdx](isFirst, isLast);

        const getBodyCellClasses = meta.getBodyCellClasses;

        const cls = classNames(
          classes.bodyCell,
          sideClasses,
          CELL_TYPE_CLASSES[type],
          meta.className,
          getBodyCellClasses?.(row),
        );

        const style: CSSProperties = {
          width: getWidthFromColumn(column),
          textAlign: textAlign,
        };
        if (column.getIsPinned() === "left") {
          const sizes = list
            .slice(0, list.indexOf(cell))
            .map((c) => c.column)
            .map(getSize);
          style.left = sizes.length > 0 && sizes.every(isNonNull) ? getWidth(sizes) : 0;
        } else if (column.getIsPinned() === "right") {
          const sizes = list
            .slice(list.indexOf(cell) + 1)
            .map((c) => c.column)
            .map(getSize);
          style.right = sizes.length > 0 && sizes.every(isNonNull) ? getWidth(sizes) : 0;
        }

        return (
          <td className={cls} key={cell.id} style={style}>
            {flexRender(column.columnDef.cell, cell.getContext())}
          </td>
        );
      })}
    </Fragment>
  ));
}
